import { ADataGridFilter } from "@atiautomacao/ati-ui-library";

/**
  * Função para mesclar e deduplicar filtros do data grid e filtros avançados ativos
  * Regras específicas:
  * - Campos multi-valor (severityName, equipmentTypeId, situationNames): consolidar valores usando ~ como separador
  * - Campo single-valor (totalizerType): substituir valor se diferente
  * - Para severityName: se hasAlarmSound estiver ativo, incluir novos valores sem remover os antigos; caso contrário, processar normalmente
  * - Para equipmentTypeId: sempre substituir se o valor for diferente
  * - Outros campos: consolidar normalmente, removendo duplicatas
  * 
  * @param dataGridFilters - Filtros atuais do data grid
  * @param activeAdvancedFilters - Filtros avançados ativos (pode ser null)
  * @param existingFilters - Filtros existentes para comparação (opcional)
  * @returns Lista de filtros mesclados e deduplicados
  */
export const mergeAndDeduplicateFilters = (
  dataGridFilters: ADataGridFilter[],
  activeAdvancedFilters: ADataGridFilter[] | null,
  existingFilters?: ADataGridFilter[]
): ADataGridFilter[] => {
  // Campos que devem ser consolidados com ~ para múltiplos valores
  const MULTI_VALUE_FIELDS = ['severityName', 'equipmentTypeId', 'situationNames'];
  // Campos que NÃO devem ter merge, apenas um valor por vez
  const SINGLE_VALUE_FIELDS = ['totalizerType'];

  // Map para rastrear campos processados: { fieldName => { oldValue?, newValue?, filter } }
  const fieldMap = new Map<string, { oldValue?: string; newValue?: string; filter: ADataGridFilter }>();

  // 1. Primeiro, mapear os filtros existentes (para comparação posterior)
  if (existingFilters) {
    for (const filter of existingFilters) {
      if (!fieldMap.has(filter.name)) {
        fieldMap.set(filter.name, {
          oldValue: filter.value?.toString(),
          filter: { ...filter }
        });
      }
    }
  }

  // 2. Processar novos filtros do data grid
  for (const filter of dataGridFilters) {
    const existing = fieldMap.get(filter.name);
    
    if (existing) {
      // Campo já existe - decidir se merge, substituir ou manter
      if (SINGLE_VALUE_FIELDS.includes(filter.name)) {
        // Substituir para campos single-value (totalizerType)
        existing.newValue = filter.value?.toString();
        existing.filter = { ...filter };
      } else if (MULTI_VALUE_FIELDS.includes(filter.name)) {
        // Consolidar para campos multi-value
        existing.newValue = filter.value?.toString();
      } else {
        // Substituir valor padrão
        existing.newValue = filter.value?.toString();
        existing.filter = { ...filter };
      }
    } else {
      // Campo novo
      fieldMap.set(filter.name, {
        newValue: filter.value?.toString(),
        filter: { ...filter }
      });
    }
  }

  // 3. Processar filtros avançados ativos
  for (const filter of activeAdvancedFilters || []) {
    const existing = fieldMap.get(filter.name);
    
    if (filter.name === 'severityName') {
      // Lógica especial para severityName
      const hasAlarmSoundFilter = fieldMap.get('hasAlarmSound');
      
      if (hasAlarmSoundFilter && hasAlarmSoundFilter.newValue === 'true') {
        // hasAlarmSound está ativo
        if (existing) {
          // Comparar se os valores de severityName são diferentes
          const oldSeverityValue = existing.oldValue || '';
          const newSeverityValue = filter.value?.toString() || '';
          
          if (oldSeverityValue !== newSeverityValue) {
            // Valores diferentes - incluir (merge)
            existing.newValue = consolidateValues(oldSeverityValue, newSeverityValue);
          }
          // Se iguais, deixa como está
        } else {
          // severityName não existe ainda
          fieldMap.set(filter.name, {
            newValue: filter.value?.toString(),
            filter: { ...filter }
          });
        }
      } else {
        // hasAlarmSound não está ativo - processar normalmente
        if (existing) {
          existing.newValue = consolidateValues(existing.newValue || '', filter.value?.toString() || '');
        } else {
          fieldMap.set(filter.name, {
            newValue: filter.value?.toString(),
            filter: { ...filter }
          });
        }
      }
    } else if (filter.name === 'equipmentTypeId') {
      // Lógica especial para equipmentTypeId - SUBSTITUIR se diferente
      if (existing) {
        const oldValue = existing.oldValue || '';
        const newValue = filter.value?.toString() || '';
        
        if (oldValue !== newValue) {
          // Valores diferentes - SUBSTITUIR
          existing.newValue = newValue;
          existing.filter = { ...filter };
        }
        // Se iguais, deixa como está
      } else {
        // equipmentTypeId não existe ainda
        fieldMap.set(filter.name, {
          newValue: filter.value?.toString(),
          filter: { ...filter }
        });
      }
    } else {
      // Outros campos - consolidar normalmente
      if (existing) {
        existing.newValue = consolidateValues(existing.newValue || '', filter.value?.toString() || '');
      } else {
        fieldMap.set(filter.name, {
          newValue: filter.value?.toString(),
          filter: { ...filter }
        });
      }
    }
  }

  // 4. Construir resultado final
  const result: ADataGridFilter[] = [];
  const entries = Array.from(fieldMap.entries());
  for (const [fieldName, data] of entries) {
    const finalValue = data.newValue !== undefined ? data.newValue : data.oldValue;
    
    // Incluir filtro se: tem valor OU tem sort definido (para ordenação sem filtro de valor)
    const hasValue = finalValue && finalValue.toString().length > 0;
    const hasSort = data.filter.sort && data.filter.sort !== 'none';
    
    if (hasValue || hasSort) {
      result.push({
        ...data.filter,
        name: fieldName,
        value: finalValue?.toString().toLowerCase() || ''
      });
    }
  }

  return result;
};

/**
 * Consolida dois valores de filtro, removendo duplicatas
 * Usa ~ como separador para múltiplos valores
 * 
 * @param oldValue - Valor antigo (pode conter ~)
 * @param newValue - Novo valor (pode conter ~)
 * @returns Valor consolidado
 */
function consolidateValues(oldValue: string, newValue: string): string {
  if (!oldValue) return newValue;
  if (!newValue) return oldValue;
  
  // Separar valores já separados
  const oldValues = oldValue.split('~').map(v => v.trim()).filter(v => v);
  const newValues = newValue.split('~').map(v => v.trim()).filter(v => v);
  
  // Combinar e remover duplicatas
  const combined = Array.from(new Set([...oldValues, ...newValues]));
  
  return combined.join('~');
}
